import { coreAtoms, type Atom, type OpCode, type ExprNode } from './runtime'
import { isBuilder } from 'tosijs-schema'
import { BUILDER_AST_VERSION, AST_VERSION_KEY } from './vm/ast-version'

type _AtomMap = typeof coreAtoms

// --- Condition String Parser ---

/**
 * Parse a simple condition string into an ExprNode.
 * Supports: identifiers, member access, binary/logical ops, literals
 * Uses the vars map to know which identifiers are state references.
 *
 * Unsupported syntax (will throw):
 * - Ternary: a ? b : c (use nested if atoms instead)
 * - Array index: a[0] (use ExprNode with computed member access)
 * - Function calls: fn(x) (use atoms)
 */
// Reserved words that shouldn't be treated as variable references
const RESERVED_WORDS = new Set([
  'true',
  'false',
  'null',
  'undefined',
  'and',
  'or',
  'not', // sometimes used as aliases
])

/**
 * VarMapping maps condition variable names to state paths.
 * Keys are variable names used in the condition string,
 * values are state paths (e.g., { count: 'counter.value' }).
 */
type VarMapping = Record<string, string>

/**
 * ItemsRef is a reference to an array in state (string path)
 * or a literal array value for iteration atoms.
 */
type ItemsRef = string | unknown[]

/**
 * Warn if condition references identifiers not present in vars mapping.
 * This helps catch common mistakes when using the Builder API.
 */
function warnMissingVars(condition: string, vars: VarMapping): void {
  // Remove string literals before scanning for identifiers
  const withoutStrings = condition
    .replace(/"[^"]*"/g, '""')
    .replace(/'[^']*'/g, "''")

  // Extract root identifiers only (not property accesses like obj.prop)
  // Match identifiers that are NOT preceded by a dot
  const identifiers: string[] = []
  const regex = /(?<![.])\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g
  let match
  while ((match = regex.exec(withoutStrings)) !== null) {
    identifiers.push(match[1])
  }
  const uniqueIds = [...new Set(identifiers)]

  const missing = uniqueIds.filter(
    (id) =>
      !RESERVED_WORDS.has(id) &&
      !(id in vars) &&
      // Ignore if it looks like a method call (followed by '(')
      !new RegExp(`\\b${id}\\s*\\(`).test(withoutStrings)
  )

  if (missing.length > 0) {
    console.warn(
      `[Agent99 Builder] Condition "${condition}" references variables not in vars mapping: ${missing.join(
        ', '
      )}. ` +
        `Add them to vars or use AsyncJS syntax (ajs\`...\`) which handles this automatically.`
    )
  }
}

function parseCondition(
  condition: string,
  vars: Record<string, any>
): ExprNode {
  // Warn about potential missing variable mappings
  warnMissingVars(condition, vars)

  const tokens = tokenize(condition)
  const result = parseExpression(tokens, 0, vars)

  // Error if there are unconsumed tokens - indicates unsupported syntax
  if (result.pos < tokens.length) {
    const remaining = tokens.slice(result.pos).join(' ')
    throw new Error(
      `Unsupported condition syntax near '${remaining}' in: ${condition}\n` +
        `Supported: comparisons, &&, ||, !, arithmetic, member access (a.b), literals`
    )
  }

  return result.node
}

function tokenize(expr: string): string[] {
  const tokens: string[] = []
  let i = 0
  while (i < expr.length) {
    // Skip whitespace
    while (i < expr.length && /\s/.test(expr[i])) i++
    if (i >= expr.length) break

    // String literals (single or double quotes)
    if (expr[i] === '"' || expr[i] === "'") {
      const quote = expr[i++]
      let str = ''
      while (i < expr.length && expr[i] !== quote) {
        if (expr[i] === '\\' && i + 1 < expr.length) {
          i++ // skip backslash
          str += expr[i++]
        } else {
          str += expr[i++]
        }
      }
      i++ // skip closing quote
      tokens.push(JSON.stringify(str)) // Store as JSON string for later parsing
      continue
    }

    // Multi-char operators
    if (expr.slice(i, i + 2).match(/^(&&|\|\||==|!=|>=|<=)$/)) {
      tokens.push(expr.slice(i, i + 2))
      i += 2
      continue
    }

    // Single-char operators (includes unsupported ?:[] for error reporting)
    if ('+-*/%><!().?:[]'.includes(expr[i])) {
      tokens.push(expr[i])
      i++
      continue
    }

    // Numbers
    if (/\d/.test(expr[i])) {
      let num = ''
      while (i < expr.length && /[\d.]/.test(expr[i])) {
        num += expr[i++]
      }
      tokens.push(num)
      continue
    }

    // Identifiers
    if (/[a-zA-Z_]/.test(expr[i])) {
      let id = ''
      while (i < expr.length && /[a-zA-Z0-9_]/.test(expr[i])) {
        id += expr[i++]
      }
      tokens.push(id)
      continue
    }

    i++
  }
  return tokens
}

function parseExpression(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  return parseLogicalOr(tokens, pos, vars)
}

function parseLogicalOr(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  let { node: left, pos: newPos } = parseLogicalAnd(tokens, pos, vars)

  while (tokens[newPos] === '||') {
    newPos++
    const { node: right, pos: rightPos } = parseLogicalAnd(tokens, newPos, vars)
    left = { $expr: 'logical', op: '||', left, right }
    newPos = rightPos
  }

  return { node: left, pos: newPos }
}

function parseLogicalAnd(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  let { node: left, pos: newPos } = parseComparison(tokens, pos, vars)

  while (tokens[newPos] === '&&') {
    newPos++
    const { node: right, pos: rightPos } = parseComparison(tokens, newPos, vars)
    left = { $expr: 'logical', op: '&&', left, right }
    newPos = rightPos
  }

  return { node: left, pos: newPos }
}

function parseComparison(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  let { node: left, pos: newPos } = parseAdditive(tokens, pos, vars)

  const compOps = ['==', '!=', '>', '<', '>=', '<=']
  while (compOps.includes(tokens[newPos])) {
    const op = tokens[newPos++]
    const { node: right, pos: rightPos } = parseAdditive(tokens, newPos, vars)
    left = { $expr: 'binary', op, left, right }
    newPos = rightPos
  }

  return { node: left, pos: newPos }
}

function parseAdditive(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  let { node: left, pos: newPos } = parseMultiplicative(tokens, pos, vars)

  while (tokens[newPos] === '+' || tokens[newPos] === '-') {
    const op = tokens[newPos++]
    const { node: right, pos: rightPos } = parseMultiplicative(
      tokens,
      newPos,
      vars
    )
    left = { $expr: 'binary', op, left, right }
    newPos = rightPos
  }

  return { node: left, pos: newPos }
}

function parseMultiplicative(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  let { node: left, pos: newPos } = parseUnary(tokens, pos, vars)

  while (
    tokens[newPos] === '*' ||
    tokens[newPos] === '/' ||
    tokens[newPos] === '%'
  ) {
    const op = tokens[newPos++]
    const { node: right, pos: rightPos } = parseUnary(tokens, newPos, vars)
    left = { $expr: 'binary', op, left, right }
    newPos = rightPos
  }

  return { node: left, pos: newPos }
}

function parseUnary(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  if (tokens[pos] === '!' || tokens[pos] === '-') {
    const op = tokens[pos++]
    const { node: argument, pos: newPos } = parseUnary(tokens, pos, vars)
    return { node: { $expr: 'unary', op, argument }, pos: newPos }
  }
  return parsePrimary(tokens, pos, vars)
}

function parsePrimary(
  tokens: string[],
  pos: number,
  vars: Record<string, any>
): { node: ExprNode; pos: number } {
  const token = tokens[pos]

  // Parenthesized expression
  if (token === '(') {
    const { node, pos: newPos } = parseExpression(tokens, pos + 1, vars)
    // Skip closing paren
    return { node, pos: newPos + 1 }
  }

  // String literal (stored as JSON)
  if (token && token.startsWith('"')) {
    return {
      node: { $expr: 'literal', value: JSON.parse(token) },
      pos: pos + 1,
    }
  }

  // Number literal
  if (token && /^\d/.test(token)) {
    return {
      node: { $expr: 'literal', value: parseFloat(token) },
      pos: pos + 1,
    }
  }

  // Boolean/null literals
  if (token === 'true')
    return { node: { $expr: 'literal', value: true }, pos: pos + 1 }
  if (token === 'false')
    return { node: { $expr: 'literal', value: false }, pos: pos + 1 }
  if (token === 'null')
    return { node: { $expr: 'literal', value: null }, pos: pos + 1 }

  // Identifier (possibly with member access via dots in vars)
  if (token && /^[a-zA-Z_]/.test(token)) {
    // Check if this identifier is in vars - if so, it's a state reference
    let node: ExprNode = { $expr: 'ident', name: token }
    let newPos = pos + 1

    // Handle member access (token.prop.subprop)
    while (tokens[newPos] === '.') {
      newPos++ // skip dot
      const prop = tokens[newPos++]
      node = { $expr: 'member', object: node, property: prop }
    }

    return { node, pos: newPos }
  }

  // Fallback - shouldn't happen
  return { node: { $expr: 'literal', value: null }, pos: pos + 1 }
}

// --- AST Types ---

export interface BaseNode {
  op: OpCode
  [key: string]: any
}

export interface SeqNode extends BaseNode {
  op: 'seq'
  steps: BaseNode[]
}

// --- Helpers ---

export interface ArgRef {
  $kind: 'arg'
  path: string
}

// --- Typed Builder ---

// Helper to extract input type from Atom definition
type AtomInput<T> = T extends Atom<infer I, any> ? I : never

// The Builder instance type with dynamic methods inferred from AtomMap
type BuilderMethods<M extends Record<string, Atom<any, any>>> = {
  [K in keyof M as M[K]['op']]: (input: AtomInput<M[K]>) => BuilderType<M>
}

// Control Flow Extensions (Custom signatures)
interface ControlFlow<M extends Record<string, Atom<any, any>>> {
  varsImport(keys: string[] | Record<string, string>): BuilderType<M>
  varsExport(keys: string[] | Record<string, string>): BuilderType<M>

  /**
   * Conditional branch. Condition is a JS-like expression string.
   * @param condition - Expression like "count > 0 && active"
   * @param vars - Map condition vars to state paths: { count: 'state.count' }
   * @param thenBranch - Steps to run if condition is true
   * @param elseBranch - Optional steps if condition is false
   */
  if(
    condition: string,
    vars: VarMapping,
    thenBranch: (b: BuilderType<M>) => BuilderType<M>,
    elseBranch?: (b: BuilderType<M>) => BuilderType<M>
  ): BuilderType<M>

  /**
   * Loop while condition is true.
   * @param condition - Expression like "count < 10"
   * @param vars - Map condition vars to state paths
   * @param body - Steps to run each iteration
   */
  while(
    condition: string,
    vars: VarMapping,
    body: (b: BuilderType<M>) => BuilderType<M>
  ): BuilderType<M>

  scope(steps: (b: BuilderType<M>) => BuilderType<M>): BuilderType<M>

  /**
   * Transform each item in array.
   * @param items - State path to array or literal array
   * @param as - Variable name for current item in scope
   * @param steps - Transform steps (result is new item value)
   */
  map(
    items: ItemsRef,
    as: string,
    steps: (b: BuilderType<M>) => BuilderType<M>
  ): BuilderType<M>

  /**
   * Filter array to items matching condition.
   * @param items - State path to array or literal array
   * @param as - Variable name for current item
   * @param condition - Filter expression using 'as' variable
   * @param vars - Additional var mappings (optional)
   */
  filter(
    items: ItemsRef,
    as: string,
    condition: string,
    vars?: VarMapping
  ): BuilderType<M>

  /**
   * Find first item matching condition.
   * @param items - State path to array or literal array
   * @param as - Variable name for current item
   * @param condition - Search expression
   * @param vars - Additional var mappings (optional)
   */
  find(
    items: ItemsRef,
    as: string,
    condition: string,
    vars?: VarMapping
  ): BuilderType<M>

  /**
   * Reduce array to single value.
   * @param items - State path to array or literal array
   * @param as - Variable name for current item
   * @param accumulator - Variable name for running result
   * @param initial - Initial accumulator value
   * @param steps - Reduction steps (result becomes new accumulator)
   */
  reduce<T>(
    items: ItemsRef,
    as: string,
    accumulator: string,
    initial: T,
    steps: (b: BuilderType<M>) => BuilderType<M>
  ): BuilderType<M>

  memoize(
    steps: (b: BuilderType<M>) => BuilderType<M>,
    key: string
  ): BuilderType<M>

  cache(
    steps: (b: BuilderType<M>) => BuilderType<M>,
    key: string,
    ttlMs?: number
  ): BuilderType<M>

  try(branches: {
    try: (b: BuilderType<M>) => BuilderType<M>
    catch?: (b: BuilderType<M>) => BuilderType<M>
  }): BuilderType<M>
}

export class TypedBuilder<M extends Record<string, Atom<any, any>>> {
  public steps: BaseNode[] = []
  private atoms: M
  private proxy: any

  constructor(atoms: M) {
    this.atoms = atoms

    // Proxy to handle dynamic atom calls
    this.proxy = new Proxy(this, {
      get: (target, prop: string | symbol, receiver) => {
        // 1. Check for class methods (as, step, toJSON, etc.)
        if (prop in target) return (target as any)[prop]

        // 2. Dynamic atom methods
        if (typeof prop === 'string' && prop in target.atoms) {
          return (input: any) => {
            const atom = target.atoms[prop]
            target.add(atom.create(input))
            return receiver
          }
        }

        return undefined
      },
    })

    return this.proxy
  }

  private add(step: BaseNode): BuilderType<M> {
    this.steps.push(step)
    return this.proxy
  }

  // --- Core Fluent API ---

  as(variableName: string): BuilderType<M> {
    if (this.steps.length === 0) throw new Error('No step to capture')
    const last = this.steps[this.steps.length - 1]
    last.result = variableName
    return this.proxy
  }

  step(node: BaseNode): BuilderType<M> {
    return this.add(node)
  }

  return(schema: any): BuilderType<M> {
    const atom = this.atoms['return']
    if (!atom) throw new Error("Atom 'return' not found")
    const _schema = isBuilder(schema) ? schema.schema : schema
    return this.add(atom.create({ schema: _schema }))
  }

  /**
   * The built AST — **the second way one is produced**, alongside the transpiler.
   *
   * Carries the format version for the same reason `emitters/ast.ts` does: the claim the
   * versioning work rests on is that *the population of unversioned ASTs stops growing*
   * (`src/vm/ast-version.ts`), and a second producer that omits the field falsifies it. The
   * builder is not a lesser path — `Agent.take(…)…toJSON()` output is handed to `vm.run` and
   * to `storeProcedure` exactly like transpiler output, so it is persisted on the same terms.
   *
   * ROOT only. Nested branches (`if`/`forEach`/`scope`/`try`) splice `.steps` directly rather
   * than calling this, so an inner `seq` never gets a field that means nothing there — the
   * version describes the document, not each node.
   */
  toJSON(): SeqNode {
    return {
      // Version first, so it survives a truncated dump and reads clearly in a diff.
      // v1: builder strings mean references (see BUILDER_AST_VERSION).
      [AST_VERSION_KEY]: BUILDER_AST_VERSION,
      op: 'seq',
      steps: [...this.steps],
    }
  }

  // --- Custom Overloads ---

  varsImport(keys: string[] | Record<string, string>) {
    return this.add(this.atoms['varsImport'].create({ keys }))
  }

  varsExport(keys: string[] | Record<string, string>) {
    return this.add(this.atoms['varsExport'].create({ keys }))
  }

  // --- Control Flow Helpers ---

  if(
    condition: string,
    vars: VarMapping,
    thenBranch: (b: BuilderType<M>) => BuilderType<M>,
    elseBranch?: (b: BuilderType<M>) => BuilderType<M>
  ) {
    const thenB = new TypedBuilder(this.atoms)
    thenBranch(thenB as any)

    let elseSteps
    if (elseBranch) {
      const elseB = new TypedBuilder(this.atoms)
      elseBranch(elseB as any)
      elseSteps = elseB.steps
    }

    // Parse condition string into ExprNode
    const conditionExpr = parseCondition(condition, vars)

    const ifAtom = this.atoms['if']
    return this.add(
      ifAtom.create({
        condition: conditionExpr,
        then: thenB.steps,
        else: elseSteps,
      })
    )
  }

  while(
    condition: string,
    vars: VarMapping,
    body: (b: BuilderType<M>) => BuilderType<M>
  ) {
    const bodyB = new TypedBuilder(this.atoms)
    body(bodyB as any)

    // Parse condition string into ExprNode
    const conditionExpr = parseCondition(condition, vars)

    const whileAtom = this.atoms['while']
    return this.add(
      whileAtom.create({
        condition: conditionExpr,
        body: bodyB.steps,
      })
    )
  }

  scope(steps: (b: BuilderType<M>) => BuilderType<M>) {
    const scopeB = new TypedBuilder(this.atoms)
    steps(scopeB as any)
    const scopeAtom = this.atoms['scope']
    return this.add(
      scopeAtom.create({
        steps: scopeB.steps,
      })
    )
  }

  map(
    items: ItemsRef,
    as: string,
    steps: (b: BuilderType<M>) => BuilderType<M>
  ) {
    const stepsB = new TypedBuilder(this.atoms)
    steps(stepsB as any)
    const mapAtom = this.atoms['map']
    return this.add(
      mapAtom.create({
        items,
        as,
        steps: stepsB.steps,
      })
    )
  }

  filter(
    items: ItemsRef,
    as: string,
    condition: string,
    vars: VarMapping = {}
  ) {
    const conditionExpr = parseCondition(condition, vars)
    const filterAtom = this.atoms['filter']
    return this.add(
      filterAtom.create({
        items,
        as,
        condition: conditionExpr,
      })
    )
  }

  find(items: ItemsRef, as: string, condition: string, vars: VarMapping = {}) {
    const conditionExpr = parseCondition(condition, vars)
    const findAtom = this.atoms['find']
    return this.add(
      findAtom.create({
        items,
        as,
        condition: conditionExpr,
      })
    )
  }

  reduce<T>(
    items: ItemsRef,
    as: string,
    accumulator: string,
    initial: T,
    steps: (b: BuilderType<M>) => BuilderType<M>
  ) {
    const stepsB = new TypedBuilder(this.atoms)
    steps(stepsB as any)
    const reduceAtom = this.atoms['reduce']
    return this.add(
      reduceAtom.create({
        items,
        as,
        accumulator,
        initial,
        steps: stepsB.steps,
      })
    )
  }

  memoize(steps: (b: BuilderType<M>) => BuilderType<M>, key: string) {
    const stepsB = new TypedBuilder(this.atoms)
    steps(stepsB as any)
    const memoAtom = this.atoms['memoize']
    return this.add(
      memoAtom.create({
        key,
        steps: stepsB.steps,
      })
    )
  }

  cache(
    steps: (b: BuilderType<M>) => BuilderType<M>,
    key: string,
    ttlMs?: number
  ) {
    const stepsB = new TypedBuilder(this.atoms)
    steps(stepsB as any)
    const cacheAtom = this.atoms['cache']
    return this.add(
      cacheAtom.create({
        key,
        steps: stepsB.steps,
        ttlMs,
      })
    )
  }

  try(branches: {
    try: (b: BuilderType<M>) => BuilderType<M>
    catch?: (b: BuilderType<M>) => BuilderType<M>
  }) {
    const tryB = new TypedBuilder(this.atoms)
    branches.try(tryB as any)

    let catchSteps
    if (branches.catch) {
      const catchB = new TypedBuilder(this.atoms)
      branches.catch(catchB as any)
      catchSteps = catchB.steps
    }

    const tryAtom = this.atoms['try']
    return this.add(
      tryAtom.create({
        try: tryB.steps,
        catch: catchSteps,
      })
    )
  }
}

// Combine dynamic atom methods with class methods
export type BuilderType<M extends Record<string, Atom<any, any>>> =
  TypedBuilder<M> & BuilderMethods<M> & ControlFlow<M>

// --- API Surface ---

export const Agent = {
  // Create a builder with default core atoms
  take(_schema?: any): BuilderType<typeof coreAtoms> {
    return new TypedBuilder(coreAtoms) as any
  },

  // Create a customized builder
  custom<M extends Record<string, Atom<any, any>>>(atoms: M): BuilderType<M> {
    return new TypedBuilder(atoms) as any
  },

  args(path: string): ArgRef {
    return { $kind: 'arg', path }
  },

  val(path: string): string {
    return path
  },
}

/** @deprecated Use `Agent` instead */
export const A99 = Agent
